import React, { useEffect, useState } from 'react';
import { Card, Typography, Button, Table, Space, Form, Input, Tag, Row, Col, message, Tooltip, Modal, Select, Tabs } from 'antd';
import { EditOutlined, ReloadOutlined, PlusCircleOutlined, DeleteOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, CombinedStates } from '../../core/types';
import { getBvmSurveysAdmin, updateBvmCategory, createOrUpdateBvmQuestion, deleteBvmQuestion } from '../../core/bvm/bvmApi';
import { BvmAdminCategory, BvmAdminQuestion } from '../../core/bvm/types';

const { Title } = Typography;
const { TextArea } = Input;

const BvmSurveyAdminListing: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const [selectedCategory, setSelectedCategory] = useState<BvmAdminCategory | null>(null);
    const [isEditingCategory, setIsEditingCategory] = useState(false);
    const [isQuestionModalVisible, setIsQuestionModalVisible] = useState(false);
    const [editingQuestion, setEditingQuestion] = useState<BvmAdminQuestion | null>(null);
    const [categoryForm] = Form.useForm();
    const [questionForm] = Form.useForm();
    const [isUpdatingQuestion, setIsUpdatingQuestion] = useState(false);

    const {
        categories,
        isFetching,
    } = useSelector((state: CombinedStates) => state.bvm);

    useEffect(() => {
        dispatch(getBvmSurveysAdmin());
    }, [dispatch]);

    const handleCategoryEdit = (category: BvmAdminCategory) => {
        setSelectedCategory(category);
        setIsEditingCategory(true);
        categoryForm.setFieldsValue({
            title: category.title,
            description: category.description,
        });
    };

    const handleCategorySave = () => {
        categoryForm
            .validateFields()
            .then((values) => {
                if (!selectedCategory) {
                    message.error('Kategorija nepasirinkta');
                    return;
                }

                dispatch(updateBvmCategory({
                    category_id: selectedCategory.id,
                    title: values.title,
                    description: values.description || '',
                }));
            })
            .catch(info => {
                console.log('Validacijos klaida:', info);
            });
    };

    const handleCategoryCancel = () => {
        setIsEditingCategory(false);
        categoryForm.resetFields();
        setSelectedCategory(null);
    };

    const handleQuestionEdit = (question: BvmAdminQuestion) => {
        setEditingQuestion(question);
        questionForm.setFieldsValue({
            code: question.code,
            title: question.title,
            description: question.description,
            type: question.type,
        });
        setIsQuestionModalVisible(true);
    };

    const handleQuestionSave = () => {
        questionForm
            .validateFields()
            .then((values) => {
                if (!selectedCategory || !editingQuestion) {
                    message.error('Kategorija arba klausimas nepasirinktas');
                    return;
                }
                setIsUpdatingQuestion(true);
                setIsQuestionModalVisible(false);
                dispatch(createOrUpdateBvmQuestion({
                    id: editingQuestion.id,
                    category_id: selectedCategory.id,
                    code: values.code,
                    title: values.title,
                    description: values.description || '',
                    type: values.type,
                }))
                    .unwrap()
                    .then(() => {
                        setIsQuestionModalVisible(false);
                        questionForm.resetFields();
                        setEditingQuestion(null);
                    })
                    .catch((error) => {
                        console.error('Klaida atnaujinant klausimą:', error);
                    })
                    .finally(() => {
                        setIsUpdatingQuestion(false);
                    });
            })
            .catch(info => {
                console.log('Validacijos klaida:', info);
            });
    };

    const handleQuestionCreate = () => {
        if (!selectedCategory) {
            message.warning('Pasirinkite kategoriją');
            return;
        }
        setEditingQuestion(null);
        questionForm.resetFields();
        setIsQuestionModalVisible(true);
    };

    const handleQuestionCreateSave = () => {
        questionForm
            .validateFields()
            .then((values) => {
                if (!selectedCategory) {
                    message.error('Kategorija nepasirinkta');
                    return;
                }
                setIsUpdatingQuestion(true);
                dispatch(createOrUpdateBvmQuestion({
                    category_id: selectedCategory.id,
                    code: values.code,
                    title: values.title,
                    description: values.description || '',
                    type: values.type,
                }))
                    .unwrap()
                    .then(() => {
                        setIsQuestionModalVisible(false);
                        questionForm.resetFields();
                    })
                    .catch((error) => {
                        console.error('Klaida kuriant klausimą:', error);
                    })
                    .finally(() => {
                        setIsUpdatingQuestion(false);
                    });
            })
            .catch(info => {
                console.log('Validacijos klaida:', info);
            });
    };

    const handleQuestionDelete = (question: BvmAdminQuestion) => {
        if (!selectedCategory) {
            message.error('Kategorija nepasirinkta');
            return;
        }

        setIsUpdatingQuestion(true);
        dispatch(deleteBvmQuestion({
            question_id: question.id,
        }))
            .unwrap()
            .then(() => {
                // Success message is handled by the reducer
            })
            .catch((error) => {
                console.error('Klaida šalinant klausimą:', error);
            })
            .finally(() => {
                setIsUpdatingQuestion(false);
            });
    };

    const getQuestionTypeColor = (type: string) => {
        switch (type) {
        case 'text':
            return 'blue';
        case 'paragraph':
            return 'orange';
        default:
            return 'default';
        }
    };

    const getQuestionTypeLabel = (type: string) => {
        switch (type) {
        case 'text':
            return 'Tekstinis atsakymas';
        case 'paragraph':
            return 'Paragrafas';
        default:
            return type;
        }
    };

    const categoryInfoTab = (
        <Card>
            <Form layout="vertical" form={categoryForm}>
                <Form.Item
                    label="Pavadinimas"
                    name="title"
                    rules={[{ required: true, message: 'Įveskite kategorijos pavadinimą' }]}>
                    <Input placeholder="Kategorijos pavadinimas" />
                </Form.Item>
                <Form.Item
                    label="Aprašymas"
                    name="description">
                    <TextArea placeholder="Kategorijos aprašymas" rows={4} />
                </Form.Item>
            </Form>
        </Card>
    );

    const questionsTab = (
        <Card>
            <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                <Col>
                    <Title level={4} style={{ margin: 0 }}>
                        Klausimai: {selectedCategory?.title}
                    </Title>
                </Col>
                <Col>
                    <Space>
                        <Button
                            type="primary"
                            icon={<PlusCircleOutlined />}
                            onClick={handleQuestionCreate}>
                            Pridėti klausimą
                        </Button>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={() => dispatch(getBvmSurveysAdmin())}
                            loading={isFetching} />
                    </Space>
                </Col>
            </Row>

            <Table
                dataSource={isFetching || isUpdatingQuestion ? [] : (categories.find(cat => cat.id === selectedCategory?.id)?.questions || [])}
                loading={isFetching || isUpdatingQuestion}
                rowKey="id"
                pagination={false}
                columns={[
                    {
                        title: 'Kodas',
                        dataIndex: 'code',
                        key: 'code',
                        width: 80,
                    },
                    {
                        title: 'Pavadinimas',
                        dataIndex: 'title',
                        key: 'title',
                    },
                    {
                        title: 'Tipas',
                        dataIndex: 'type',
                        key: 'type',
                        width: 150,
                        render: (type: string) => (
                            <Tag color={getQuestionTypeColor(type)}>
                                {getQuestionTypeLabel(type)}
                            </Tag>
                        ),
                    },
                    {
                        title: 'Aprašymas',
                        dataIndex: 'description',
                        key: 'description',
                        ellipsis: true,
                    },
                    {
                        title: 'Veiksmai',
                        key: 'actions',
                        width: 100,
                        render: (_, record: BvmAdminQuestion) => (
                            <Space>
                                <Tooltip title="Redaguoti klausimą">
                                    <Button
                                        size="small"
                                        icon={<EditOutlined />}
                                        onClick={() => handleQuestionEdit(record)} />
                                </Tooltip>
                                <Tooltip title="Pašalinti klausimą">
                                    <Button
                                        size="small"
                                        danger
                                        icon={<DeleteOutlined />}
                                        onClick={() => {
                                            Modal.confirm({
                                                title: 'Patvirtinkite veiksmą',
                                                content: 'Ar tikrai norite pašalinti šį klausimą?',
                                                okText: 'Taip, pašalinti',
                                                cancelText: 'Atšaukti',
                                                okType: 'danger',
                                                onOk() {
                                                    handleQuestionDelete(record);
                                                },
                                            });
                                        }} />
                                </Tooltip>
                            </Space>
                        ),
                    },
                ]} />
        </Card>
    );

    const tabItems = [
        {
            key: '1',
            label: 'Kategorijos informacija',
            children: categoryInfoTab,
        },
        {
            key: '2',
            label: 'Klausimai',
            children: questionsTab,
        },
    ];

    const categoriesTab = (
        <Card>
            <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                <Col>
                    <Title level={4} style={{ margin: 0 }}>
                        {isEditingCategory ? 'Redaguoti kategoriją' : 'BVM Apklausų kategorijos'}
                    </Title>
                </Col>
                <Col>
                    <Space>
                        {isEditingCategory ? (
                            <>
                                <Button onClick={handleCategoryCancel}>
                                    Grįžti į apklausų kategorijas
                                </Button>
                                <Button loading={isFetching} type="primary" onClick={handleCategorySave}>
                                    Išsaugoti
                                </Button>
                            </>
                        ) : (
                            <Tooltip title="Atnaujinti sąrašą">
                                <Button
                                    icon={<ReloadOutlined />}
                                    onClick={() => dispatch(getBvmSurveysAdmin())}
                                    loading={isFetching} />
                            </Tooltip>
                        )}
                    </Space>
                </Col>
            </Row>

            {isEditingCategory ? (
                <Tabs defaultActiveKey="1" items={tabItems} type="card" className="categories-tabs" />
            ) : (
                <Table
                    dataSource={categories}
                    loading={isFetching}
                    rowKey="id"
                    pagination={false}
                    columns={[
                        {
                            title: 'Nr.',
                            key: 'order',
                            width: 60,
                            render: (_, __, index) => index + 1,
                        },
                        {
                            title: 'Pavadinimas',
                            dataIndex: 'title',
                            key: 'title',
                        },
                        {
                            title: 'Aprašymas',
                            dataIndex: 'description',
                            key: 'description',
                            ellipsis: true,
                        },
                        {
                            title: 'Klausimų skaičius',
                            key: 'questions_count',
                            width: 120,
                            render: (_, record: BvmAdminCategory) => (
                                <Tag color="blue">{record.questions?.length || 0}</Tag>
                            ),
                        },
                        {
                            title: 'Veiksmai',
                            key: 'actions',
                            width: 100,
                            render: (_, record: BvmAdminCategory) => (
                                <Space>
                                    <Tooltip title="Redaguoti kategoriją">
                                        <Button
                                            size="small"
                                            icon={<EditOutlined />}
                                            onClick={() => handleCategoryEdit(record)} />
                                    </Tooltip>
                                </Space>
                            ),
                        },
                    ]} />
            )}
        </Card>
    );

    return (
        <div style={{ marginTop: '-20px' }}>
            {categoriesTab}

            {/* Question Create/Edit Modal */}
            <Modal
                title={editingQuestion ? 'Redaguoti klausimą' : 'Sukurti naują klausimą'}
                visible={isQuestionModalVisible}
                onOk={editingQuestion ? handleQuestionSave : handleQuestionCreateSave}
                onCancel={() => {
                    setIsQuestionModalVisible(false);
                    questionForm.resetFields();
                    setEditingQuestion(null);
                }}
                okText={editingQuestion ? 'Atnaujinti' : 'Sukurti'}
                loading={isFetching}
                cancelText="Atšaukti">
                <Form layout="vertical" form={questionForm}>
                    <Form.Item
                        label="Kodas"
                        name="code"
                        rules={[{ required: true, message: 'Įveskite klausimo kodą' }]}>
                        <Input placeholder="Pvz: Q1, Q2" />
                    </Form.Item>
                    <Form.Item
                        label="Pavadinimas"
                        name="title"
                        rules={[{ required: true, message: 'Įveskite klausimo pavadinimą' }]}>
                        <Input placeholder="Klausimo pavadinimas" />
                    </Form.Item>
                    <Form.Item
                        label="Tipas"
                        name="type"
                        rules={[{ required: true, message: 'Pasirinkite klausimo tipą' }]}>
                        <Select placeholder="Pasirinkite tipą">
                            <Select.Option value="text">Tekstinis atsakymas</Select.Option>
                            <Select.Option value="paragraph">Paragrafas</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item
                        label="Aprašymas"
                        name="description">
                        <TextArea placeholder="Klausimo aprašymas" rows={4} />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default BvmSurveyAdminListing;
